import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import PropTypes from 'prop-types';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';

import { create, update } from 'apis/proposalPrograms.api';
import { DatePicker, TextareaInput } from 'components/FormControl';
import Loading from 'components/Loading';
import { TOAST } from 'constant';
import createProgramValidationSchema from 'features/ProgramProposal/validation';
import { toast } from 'layout';
import { getEndingTimeOfDate, mapError } from 'utils/func';

const formOptions = { resolver: yupResolver(createProgramValidationSchema) };

const Form = forwardRef(({ refetch }, ref) => {
  // #region Data
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [programControl, setProgramControl] = useState(null);

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    formState: { errors, dirtyFields, isDirty },
  } = useForm({
    ...formOptions,
    mode: 'onChange',
  });

  const { mutate: mutateCreate, isLoading: isLoadingCreate } = useMutation(create);
  const { mutate: mutateUpdate, isLoading: isLoadingUpdate } = useMutation(update);

  // #endregion

  // #region Event
  useImperativeHandle(
    ref,
    () => ({
      open: (_programControl) => {
        setProgramControl(_programControl);
        setVisible(true);
        if (_programControl.mode === 'CREATE') {
          reset({});
        } else if (_programControl.mode === 'UPDATE') {
          const { startTime, endTime, ...rest } = _programControl.value;

          reset({
            ...rest,
            startTime: startTime ? new Date(startTime) : null,
            endTime: endTime ? new Date(endTime) : null,
          });
        }
      },
    }),
    []
  );

  const onSubmit = (data) => {
    const startTime = data?.startTime ? new Date(data.startTime) : null;
    const endTime = data?.endTime ? getEndingTimeOfDate(new Date(data.endTime)) : null;

    if (programControl.mode === 'CREATE') {
      const newData = {
        ...data,
        startTime,
        endTime,
      };

      mutateCreate(newData, {
        onSuccess: () => {
          toast(TOAST.SUCCESS, t('programProposal.create.createSuccess'));
          reset();
          refetch();
          setVisible(false);
        },
        onError: (error) => {
          toast(TOAST.ERROR, mapError(t, error));
        },
      });
    } else {
      const { id, ...rest } = {
        ...data,
        startTime,
        endTime,
      };
      const newData = Object.keys(dirtyFields)?.reduce((resultObj, current) => {
        // eslint-disable-next-line no-param-reassign
        resultObj[current] = rest[current];
        return resultObj;
      }, {});

      mutateUpdate(
        { id, data: newData },
        {
          onSuccess: () => {
            toast(TOAST.SUCCESS, t('programProposal.update.updateSuccess'));
            refetch();
            setVisible(false);
          },
          onError: (error) => {
            toast(TOAST.ERROR, mapError(t, error));
          },
        }
      );
    }
  };

  const handleDeadlineTimeChange = async () => {
    await trigger('startTime');
    await trigger('endTime');
  };

  const checkFormValid = async () => {
    const isValid = await trigger();
    if (!isValid) toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
  };

  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };
  // #endregion

  return (
    <Dialog
      header={
        // eslint-disable-next-line react/jsx-wrap-multilines
        <p className="text-blue-500">
          {programControl?.mode === 'CREATE'
            ? t('programProposal.create.title')
            : t('programProposal.update.title')}
        </p>
      }
      visible={visible}
      style={{ maxWidth: '500px' }}
      draggable={false}
      onHide={() => {
        reset({});
        setVisible(false);
      }}
    >
      <div className="relative">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={checkKeyDown}
          className="p-fluid flex flex-column"
          autoComplete="off"
        >
          <div className="grid">
            <div className="col-12">
              <TextareaInput
                label={t('programProposal.name')}
                name="name"
                autoFocus
                control={control}
                errors={errors}
                isRequired
              />
            </div>
            <div className="col-12 mb-2">
              <TextareaInput
                label={t('programProposal.description')}
                name="description"
                control={control}
                errors={errors}
                row={2}
                isRequired
              />
            </div>
            <div className="col-6">
              <DatePicker
                label={t('programProposal.startingTime')}
                name="startTime"
                control={control}
                errors={errors}
                isRequired
                onCustomChange={handleDeadlineTimeChange}
              />
            </div>
            <div className="col-6">
              <DatePicker
                label={t('programProposal.endingTime')}
                name="endTime"
                control={control}
                errors={errors}
                isRequired
                onCustomChange={handleDeadlineTimeChange}
              />
            </div>
          </div>

          <div className="grid mt-2">
            <div className="col-6">
              <Button
                label={t('programProposal.cancel')}
                type="button"
                severity="danger"
                onClick={() => {
                  reset({});
                  setVisible(false);
                }}
              />
            </div>
            <div className="col-6">
              <Button
                label={
                  programControl?.mode === 'CREATE'
                    ? t('programProposal.add')
                    : t('programProposal.save')
                }
                icon="pi pi-pencil"
                className="bg-cyan-400 border-cyan-400 hover:bg-cyan-500"
                type="submit"
                onClick={checkFormValid}
                disabled={!isDirty || !(Object.keys(dirtyFields).length > 0)}
              />
            </div>
          </div>
        </form>
        {(isLoadingCreate || isLoadingUpdate) && <Loading />}
      </div>
    </Dialog>
  );
});

export default Form;

Form.propTypes = {
  refetch: PropTypes.func.isRequired,
};
